import { createContext, useCallback, useContext, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Alert, DialogContent } from '@mui/material';
import { ModalAnnouncementProps, RenderModalProps, ModalContextValues, modalComponents } from './types';
import { useAppSelector } from '@reduxStore';
import ApplicationModal from './ApplicationModal';

const ModalContext = createContext<ModalContextValues>(null);

/**
 * Component that handles reading the modal queue and rendering modal windows for the program
 */
export default function ModalProvider() {
  const { renderQueue } = useAppSelector((state) => state.modalSlice);

  if (renderQueue.length < 1) {
    return <></>;
  }

  return renderQueue.map((component) => (
    <ModalContextProvider
      key={`modal-${String(component.id)}`}
      component={component}
    />
  ));
}

/**
 * React Context wrapped around the rendered Modal window allowing the lower tree components to communicate with higher level components
 */
function ModalContextProvider({ component }: { component: RenderModalProps }) {
  const [modalAnnouncements, setModalAnnouncements] = useState<ModalAnnouncementProps[]>([]);

  const queueAnnouncement = useCallback((announcement: Omit<ModalAnnouncementProps, 'id'>) => {
    setModalAnnouncements((state) => [
      ...state,
      {
        id: Math.floor(Math.random() * 10000),
        ...announcement
      }
    ]);
  }, []);

  const contextValues: ModalContextValues = {
    modalAnnouncements: modalAnnouncements,
    queueModalAnnouncement: queueAnnouncement,
    removeModalAnnouncement: (id: number) => {
      setModalAnnouncements((state) => [...state.filter((a) => a.id !== id)]);
    },
    reset: () => {
      setModalAnnouncements([]);
    }
  };

  return (
    <ModalContext.Provider value={contextValues}>
      {/* Without this, a modal render error bubbles to the router boundary and replaces the whole layout */}
      <ErrorBoundary FallbackComponent={ModalErrorFallback}>
        <RenderModal component={component} />
      </ErrorBoundary>
    </ModalContext.Provider>
  );
}

function ModalErrorFallback() {
  const [open, setOpen] = useState(true);

  return (
    <ApplicationModal
      isOpen={open}
      title='Error'
      onClose={() => {
        setOpen(false);
      }}
    >
      <DialogContent>
        <Alert severity='error'>This window could not be loaded. Close it and try again, or contact support if the problem continues.</Alert>
      </DialogContent>
    </ApplicationModal>
  );
}

export function useModalProvider() {
  const modalContext = useContext(ModalContext);

  if (modalContext === null) {
    throw new Error('useModalProvider must be invoked within a ModalContextProvider');
  }

  return modalContext;
}

export function useNullableModalProvider() {
  const modalContext = useContext(ModalContext);
  return modalContext;
}

/**
 * Type Guard function to ensure Props are typed correctly when rendering the modal element. Currently this needs to be updated with each new modal type until
 * a better generic solution can be determined.
 * @returns
 */
function RenderModal(props: { component: RenderModalProps }) {
  const { Component } = props.component;

  switch (Component) {
    case 'consigneeModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'addDocumentModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'editDocumentModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'emailDocumentsModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'makeAvailableModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'bulkMakeAvailableModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'managePudlModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'updateScheduledPudlModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'sendPudlReceiptModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'viewDispatchEventsModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'viewItemLocationHistoryModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'confirmPopup': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'consignmentDetails': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent />;
    }

    case 'chargeForm': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'paymentForm': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'railBillingModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'streetTurnModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'contactForm': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'requestCredit': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'documentControlModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent />;
    }

    case 'multiContainerUpdate': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'addWorkOrderContact': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'addConsignment': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'reminderModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'reportModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent />;
    }

    case 'ediLoadTenderModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent />;
    }

    case 'addVessel': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'vesselVoyage': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'ediRejectLoadTenderModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'copyOrderPrompt': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'cancelReasonPrompt': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'terminalPreferences': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent />;
    }

    case 'copyQueryToSite': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    case 'viewDriverRouteModal': {
      const RenderModalComponent = modalComponents[Component];
      return <RenderModalComponent {...props.component.Props} />;
    }

    default: {
      throw new Error('Cannot render unknown modal.');
    }
  }
}
